import {
  Image,
  Box,
  Button,
  Center,
  Flex,
  Text,
  Link,
  Show,
  Spinner,
  IconButton,
  Divider,
  Progress,
} from "@chakra-ui/react";
import { TiAnchorOutline, TiChartBar, TiCogOutline, TiMessages } from "react-icons/ti";
import { AiFillAudio } from "react-icons/ai";
import { useRouter } from "next/router";
import { FiBook } from "react-icons/fi";
import { useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { HiOutlineMenuAlt2 } from "react-icons/hi";
import { FaGraduationCap, FaRegStar } from "react-icons/fa";
import { addBreadcrumb, captureException } from "@sentry/nextjs";
import { MdLogout } from "react-icons/md";
import { useHotkeys } from "react-hotkeys-hook";

import { useSettings } from "../../hooks/use-settings";
import Analytics from "../analytics";
import { useUser } from "../../hooks/use-user";
import BetaPassword from "../beta-password";
import { detectIOS } from "../../utils/detect-ios";
import { useConversionTracker } from "../../hooks/use-conversion-tracker";
import AppProvider from "../../pages/app-provider";
import { isChina } from "../../utils/is-china";
import useWindowHeight from "../../hooks/use-window-height";
import { useUserGroups } from "../../hooks/use-user-groups";
import { useMixpanel } from "../../hooks/use-mixpanel";
import { siteConfig } from "../../configuration/config";
import { useLocalization } from "../../hooks/use-localization";
import { logger } from "../../utils/logger";
import { useLanguageLock } from "../../hooks/use-language-lock";
import { useVoiceSelection } from "../../hooks/use-voice-selection";
import ErrorBoundary from "../error-boundary";
import { logout } from "../../utils/logout";
import { interpolateString } from "../../utils/interpolate-string";
import SidebarButton from "../sidebar-button";
import { useIntroRedirect } from "../../hooks/use-intro-redirect";
import { useRighToLeftLanguage } from "../../hooks/use-right-to-left-language";
import { appSignInRedirect } from "../../types/constants";
import OrganizationSwitcher from "../organization/organization-switcher";
import { useOrganizationSettings } from "../../hooks/use-organization-settings";
import LanguageConfigPromptModal from "../language-config-prompt-modal";
import { BsClipboardData } from "react-icons/bs";
import IdleDetector from "../../components/user-activity/idle-detector";
import { fetchApi } from "services/fetch-api";
import MobileNavigationMenu from "./mobile-navigation-menu";
import { useMobileNav } from "./mobile-nav-context";

interface LayoutApp {
  children: React.ReactNode;
}

type MenuItemType = {
  label: string;
  href: string;
  icon?: ReactNode;
  show: boolean;
  mobileOnly?: boolean;
};

const getMenuItems = (
  l: Record<string, string>,
  siteConfig: any,
  hasGroups: boolean,
  user: any,
  interpolateString: (str: string, vars: Record<string, string>) => string,
): MenuItemType[] => [
  {
    label: l["layout.home"],
    href: "/app/",
    icon: <TiAnchorOutline />,
    show: true,
  },
  {
    label: l["layout.chat"],
    href: "/app/chat?free=true",
    icon: <TiMessages />,
    show: true,
  },
  {
    label: l["layout.pronunciation"],
    href: "/app/pronunciation",
    icon: <AiFillAudio />,
    show: true,
  },
  {
    label: l["layout.wordbook"],
    href: "/app/wordbook",
    icon: <FiBook />,
    show: true,
  },
  {
    label: l["layout.progress"],
    href: "/app/progress",
    icon: <TiChartBar />,
    show: !siteConfig.hideProgressMenuLink,
  },
  {
    label: l["layout.programs"],
    href: "/app/programs",
    icon: <FaGraduationCap />,
    show: hasGroups,
  },
  {
    label: l["layout.settings"],
    href: "/app/settings",
    icon: <TiCogOutline />,
    show: true,
  },
  {
    label: l["layout.account"],
    href: "/app/account",
    icon: null,
    show: !siteConfig.hideManageAccountLink,
    mobileOnly: true,
  },
  {
    label: siteConfig.parentService
      ? interpolateString(l["layout.parentServiceLink"], { name: siteConfig.parentService.name })
      : "",
    href:
      siteConfig.parentService && typeof siteConfig.parentService.link === "string"
        ? siteConfig.parentService.link
        : "",
    icon: <MdLogout />,
    show: !!siteConfig.parentService,
  },
  {
    label: "Learning Hub",
    href: "/hub",
    icon: <BsClipboardData />,
    show: Boolean(user?.facilitatedOrganizations?.length),
  },
];

// Helper function for active state
function isMenuItemActive(currentPath: string, itemHref: string): boolean {
  const cleanCurrent = currentPath.split("?")[0].replace(/\/$/, "");
  const cleanHref = itemHref.split("?")[0].replace(/\/$/, "");
  if (cleanHref === "/app") {
    return cleanCurrent === "/app";
  }
  return cleanCurrent.startsWith(cleanHref);
}

// Helper component for manage account link that uses mobile nav context
function ManageAccountLink({ l }: { l: Record<string, string> }) {
  const router = useRouter();
  const { closeMobileNav } = useMobileNav();

  const handleClick = () => {
    if (closeMobileNav) {
      closeMobileNav();
    }
    router.push("/app/account");
  };

  return (
    <Link
      color={"gray.400"}
      display={"flex"}
      fontWeight={"bold"}
      fontSize={"small"}
      cursor="pointer"
      onClick={handleClick}
    >
      {l["layout.manageAccount"]}
    </Link>
  );
}

export default function LayoutApp({ children }: LayoutApp) {
  const localization = useLocalization();
  const l = localization.l;
  const [didLoad, setDidLoad] = useState(false);
  const [showIdleIndicator, setShowIdleIndicator] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const innerHeight = useWindowHeight();
  const { user, userIsLoading, systemUser, subscriptionIsActive } = useUser();
  const { hasGroups } = useUserGroups();
  const { redirectingToIntro } = useIntroRedirect();

  const {
    settings,
    settingsAreLoading,
    settingsError,
    userNotLoggedIn,
    settingsAreValidating,

    saveInitialSettings,
  } = useSettings();
  const { organizationSettings, organizationSettingsAreLoading } = useOrganizationSettings();

  const router = useRouter();
  const { isRightToLeftPracticeLanguage } = useRighToLeftLanguage({});
  const initialSettingSaved = useRef(false);

  useConversionTracker();
  useMixpanel(true);
  useLanguageLock();
  const { isLoadingVoiceSelection } = useVoiceSelection();
  useHotkeys("shift+i+d+l+e", () => {
    setShowIdleIndicator(!showIdleIndicator);
  });

  // Handle router events to show/hide loader
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleStart = () => {
      setIsLoading(true);
      // Fallback timeout to ensure loader stops even if events don't fire
      timeoutId = setTimeout(() => {
        setIsLoading(false);
      }, 10000); // 10 second timeout
    };

    const handleComplete = () => {
      setIsLoading(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [router]);

  useEffect(() => {
    if (!userIsLoading && user && process.env.NEXT_PUBLIC_PARTNER_DOMAIN) {
      const message = `Checking user provider. Env partner: ${process.env.NEXT_PUBLIC_PARTNER_DOMAIN}, User provider: ${user.provider}.`;
      logger.info(message);

      addBreadcrumb({
        category: "log",
        message,
        level: "info",
      });

      // Special cases for punkify and clubedealemao
      if (
        user.provider === "punkify.com" &&
        process.env.NEXT_PUBLIC_PARTNER_DOMAIN === "clubedealemao.com"
      ) {
        addBreadcrumb({
          category: "log",
          message: "Allow punkify user on clubedealemao",
          level: "info",
        });

        return;
      } else if (
        user.provider === "clubedealemao.com" &&
        process.env.NEXT_PUBLIC_PARTNER_DOMAIN === "punkify.com"
      ) {
        addBreadcrumb({
          category: "log",
          message: "Allow clubedealemao user on punkify",
          level: "info",
        });
        return;
      }

      if (user?.provider !== process.env.NEXT_PUBLIC_PARTNER_DOMAIN) {
        const message = `User provider [${user?.provider}] does not match partner domain [${process.env.NEXT_PUBLIC_PARTNER_DOMAIN}]. Signing out.`;

        logger.warn(message);

        // captureException(message, {
        //   extra: {
        //     origin: "layout-app.tsx",
        //   },
        // });

        // logout({ callbackUrl: "/authentication/partner/error" });
      }
    }
  }, [user, userIsLoading, router]);

  useEffect(() => {
    if (
      initialSettingSaved.current ||
      settingsAreValidating ||
      settingsAreLoading ||
      organizationSettingsAreLoading ||
      !systemUser
    ) {
      return;
    }

    if (!settings.id) {
      initialSettingSaved.current = true;
      saveInitialSettings();
    }
  }, [
    settingsAreValidating,
    settingsAreLoading,
    systemUser,
    settings,
    organizationSettingsAreLoading,
    saveInitialSettings,
  ]);

  useEffect(() => {
    if (!didLoad && user && settings?.id) {
      setDidLoad(true);
    }
  }, [user, settings, didLoad]);

  useEffect(() => {
    if (router.pathname.includes("/app/chat") && detectIOS()) {
      document.body.classList.add("chat-layout-ios");
    }
  }, [router.pathname]);

  useEffect(() => {
    // Store user timezone on login if not set or changed
    if (user && typeof window !== "undefined") {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedTz && detectedTz.length <= 64 && user.timezone !== detectedTz) {
        fetchApi("/user/timezone", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ timezone: detectedTz }),
        }).catch((error) => {
          logger.error("Error updating user timezone", error);
        });
      }
    }
  }, [user]);

  const shouldOpenLanguageConfigModal = useMemo(() => {
    // Return false if loading
    if (
      settingsAreLoading ||
      organizationSettingsAreLoading ||
      organizationSettings?.supportedLanguages === undefined ||
      organizationSettings?.supportedLanguages === null
    ) {
      return false;
    }

    // Empty list means no languages configured eg. all enabled
    if (organizationSettings.supportedLanguages.length === 0) {
      return false;
    }

    const practiceLanguageExistsInOrgLangList = organizationSettings.supportedLanguages.includes(
      settings.practiceLanguage,
    );

    return !practiceLanguageExistsInOrgLangList;
  }, [
    organizationSettings,
    settingsAreLoading,
    organizationSettingsAreLoading,
    settings.practiceLanguage,
  ]);

  const menuItems = useMemo(
    () =>
      getMenuItems(l, siteConfig, hasGroups, user, interpolateString).filter(
        (item) => item.show && item.label && item.href,
      ),
    [l, hasGroups, user],
  );

  const desktopMenuItems = menuItems.filter((item) => !item.mobileOnly); // exclude mobileOnly items

  if (userNotLoggedIn) {
    window.location.href = appSignInRedirect;
    return null;
  }

  if (userIsLoading || settingsAreLoading || redirectingToIntro) {
    return (
      <Flex
        w={"100%"}
        h={"100vh"}
        alignItems={"center"}
        justifyContent={"center"}
        bg="brand.gray.900"
      >
        <Spinner size="xl" color={"brand.light.main"} />
      </Flex>
    );
  }

  if (!didLoad) {
    if (!user && typeof window !== "undefined") {
      window.location.href = appSignInRedirect;
      return <div />;
    }

    if (settingsError) {
      return (
        <Flex
          w={"100%"}
          h={"100vh"}
          alignItems={"center"}
          justifyContent={"center"}
          bg="brand.gray.900"
          color="white"
        >
          <Text>{l["layout.unableToConnect"]}</Text>
        </Flex>
      );
    }

    if (!subscriptionIsActive && typeof window !== "undefined") {
      window.location.href = "/billing";
      return <div />;
    }
  }

  if (user?.paymentFailed && router.pathname !== "/app/account" && typeof window !== "undefined") {
    window.location.href = "/app/account";
    return <div />;
  }

  return (
    <Box className={`${isRightToLeftPracticeLanguage ? "rtl-practice-language" : ""}`}>
      <AppProvider localization={localization}>
        {/* <AdjustHeight /> */}
        <BetaPassword />
        {siteConfig.isDefault && <Analytics gKey={"G-38FBRZ326Q"} />}

        {/* Global loader */}
        {isLoading && (
          <Progress
            height="2px"
            isIndeterminate
            colorScheme="brand.primary"
            position="fixed"
            top="0"
            left="0"
            right="0"
            zIndex="9999"
            bg="transparent"
          />
        )}
        <LanguageConfigPromptModal
          isLoadingVoiceSelection={isLoadingVoiceSelection}
          practiceLanguageNeedsConfig={shouldOpenLanguageConfigModal}
        />
        <Show below="md">
          <Flex
            flexDir={"column"}
            id="mobile-wrapper"
            data-page={router.pathname.substring(1).replace(/\//g, "-")}
            height={innerHeight}
          >
            <Box
              backgroundColor={"special.mobileNavBackgroundColor"}
              id="mobile-header-spacer"
              h="50px"
            ></Box>
            <Flex
              borderBottom="1px solid"
              borderColor="special.mobileNavBorderColor"
              h={"50px"}
              backgroundColor={"special.mobileNavBackgroundColor"}
              justifyContent={"space-between"}
              alignItems="center"
              position="fixed"
              w="full"
              zIndex={20}
              id="mobile-header"
              top="0"
            >
              <Box hidden={siteConfig.hideNavigation}>
                <MobileNavigationMenu>
                  <Flex
                    h={innerHeight}
                    w="240px"
                    backgroundColor={"special.desktopSidebar"}
                    color={"white"}
                    flexDirection={"column"}
                    justifyContent={"space-between"}
                    borderRightWidth={"1px"}
                    borderRightColor="special.desktopSidebarBorderColor"
                    overflowY="auto"
                    overflowX="hidden"
                    maxH="100%"
                  >
                    <Box>
                      <Center mt={"desktopSidebarLogoTopOffset"} mb={12}>
                        <Image
                          maxH={{ base: "100%" }}
                          h={siteConfig.appLogoHeight}
                          src={siteConfig.appLogoPath}
                          alt={siteConfig.serviceName}
                          opacity={siteConfig.isDefault ? 0.5 : 1}
                        />
                      </Center>

                      <Flex
                        hidden={siteConfig.hideNavigation}
                        px="15px"
                        justifyContent={"flex-start"}
                        alignItems={"center"}
                        flexDirection={"column"}
                      >
                        {desktopMenuItems.map((item) => (
                          <SidebarButton
                            key={item.href}
                            icon={item.icon}
                            href={item.href}
                            label={item.label}
                            active={isMenuItemActive(router.asPath, item.href)}
                          />
                        ))}
                      </Flex>
                    </Box>
                    <Box>
                      <Flex
                        px="15px"
                        justifyContent={"flex-start"}
                        data-testid="organization-switcher"
                        alignItems={"center"}
                        flexDirection={"column"}
                      >
                        <OrganizationSwitcher />
                      </Flex>
                      {showIdleIndicator && (
                        <Box px="15px" justifyContent={"flex-start"}>
                          <IdleDetector />
                        </Box>
                      )}
                      <Divider my={3} />
                      {!siteConfig.hideManageAccountLink || siteConfig.reviewLink ? (
                        <Flex
                          flexDirection={"column"}
                          justifyContent={"center"}
                          alignItems={"center"}
                          py={3}
                        >
                          {!siteConfig.hideManageAccountLink && <ManageAccountLink l={l} />}

                          {siteConfig.reviewLink && (
                            <Flex
                              as="a"
                              href={siteConfig.reviewLink}
                              target="_blank"
                              rel="nofollow noreferrer"
                              pt={2}
                              fontSize={"2xs"}
                              color={"gray.500"}
                              _hover={{ textDecor: "underline" }}
                            >
                              <Box pt={"3px"}>
                                <FaRegStar />
                              </Box>
                              <Text ml={1}>{l["layout.leaveReview"]}</Text>
                            </Flex>
                          )}
                        </Flex>
                      ) : null}
                    </Box>
                  </Flex>
                </MobileNavigationMenu>
              </Box>
              <Box flexGrow={1}>
                <Center>
                  {siteConfig.mobileLogoPath && (
                    <Image
                      className="app-mobile-logo"
                      h={"26px"}
                      my={"12px"}
                      mr="80px"
                      src={siteConfig.mobileLogoPath}
                      alt={siteConfig.serviceName}
                    />
                  )}
                </Center>
              </Box>
            </Flex>

            <Box h={innerHeight - 50} bg="brand.light.main">
              <ErrorBoundary>{children}</ErrorBoundary>
            </Box>
          </Flex>
        </Show>
        <Show above="md">
          <Flex>
            <Flex
              id="desktop-sidebar"
              flex="1"
              h="100vh"
              bg="white"
              maxW="200px"
              backgroundColor={"special.desktopSidebar"}
              color={"white"}
              flexDirection={"column"}
              justifyContent={"space-between"}
              borderRightWidth={"1px"}
              borderRightColor="special.desktopSidebarBorderColor"
              overflowY="auto"
              overflowX="hidden"
              maxH="100%"
            >
              <Box>
                <Center mt={"desktopSidebarLogoTopOffset"} mb={12}>
                  <Image
                    maxH={{ base: "100%" }}
                    h={siteConfig.appLogoHeight}
                    src={siteConfig.appLogoPath}
                    alt={siteConfig.serviceName}
                    opacity={siteConfig.isDefault ? 0.5 : 1}
                  />
                </Center>

                <Box hidden={siteConfig.hideNavigation} px="15px" justifyContent={"flex-start"}>
                  {desktopMenuItems.map((item) => (
                    <SidebarButton
                      key={item.href}
                      icon={item.icon}
                      href={item.href}
                      label={item.label}
                      active={isMenuItemActive(router.asPath, item.href)}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Box px="15px" justifyContent={"flex-start"} data-testid="organization-switcher">
                  <OrganizationSwitcher />
                </Box>
                {showIdleIndicator && (
                  <Box px="15px" justifyContent={"flex-start"}>
                    <IdleDetector />
                  </Box>
                )}
                <Divider my={3} />
                {!siteConfig.hideManageAccountLink || siteConfig.reviewLink ? (
                  <Flex
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    py={3}
                  >
                    {!siteConfig.hideManageAccountLink && <ManageAccountLink l={l} />}

                    {siteConfig.reviewLink && (
                      <Flex
                        as="a"
                        href={siteConfig.reviewLink}
                        target="_blank"
                        rel="nofollow noreferrer"
                        pt={2}
                        fontSize={"2xs"}
                        color={"gray.500"}
                        _hover={{ textDecor: "underline" }}
                      >
                        <Box pt={"3px"}>
                          <FaRegStar />
                        </Box>
                        <Text ml={1}>{l["layout.leaveReview"]}</Text>
                      </Flex>
                    )}
                  </Flex>
                ) : null}
              </Box>
            </Flex>

            <Box flex="2" h="100vh" bg="brand.light.alt">
              <ErrorBoundary>{children}</ErrorBoundary>
            </Box>
          </Flex>
        </Show>
      </AppProvider>
    </Box>
  );
}
